import { Box, Center, Divider, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { Stars03 } from '@kula-ai/icons-core';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { KText } from '@/ui-library/components/text';
import { AutoRejectSection } from '@/components/shared/ai-scoring/AiScoringDrawer/AutoRejectSection';

interface Attribute {
  id: number | string;
  description?: string;
  position: number;
}

interface ScreenerInfoContentProps {
  attributes: Attribute[];
  autoReject: boolean;
  autoRejectThreshold: number;
  onAutoRejectChange: (_value: boolean) => void;
  onThresholdChange: (_value: number) => void;
  /** Opens the existing attributes editor (setup wizard), mirroring the old AI scoring UI. */
  onEditAttributes?: () => void;
  isSaving?: boolean;
  /**
   * Rejection-email island rendered under the auto-reject section (ENG-3114).
   * Passed as a slot so this component stays decoupled from the AiScoring page.
   */
  autoRejectEmailSlot?: React.ReactNode;
}

export const ScreenerInfoContent = ({
  attributes,
  autoReject,
  autoRejectThreshold,
  onAutoRejectChange,
  onThresholdChange,
  onEditAttributes,
  isSaving = false,
  autoRejectEmailSlot,
}: ScreenerInfoContentProps) => {
  const sortedAttributes = Array.isArray(attributes)
    ? [...attributes].sort((a, b) => a.position - b.position)
    : [];

  return (
    <Flex
      flexDir="column"
      position="relative"
      opacity={isSaving ? 0.6 : 1}
      pointerEvents={isSaving ? 'none' : 'auto'}
      transition="opacity 0.2s"
    >
      {/* Candidate attributes header with edit affordance */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        p="16px 24px 12px"
      >
        <KText variant="heading_4">
          Candidate attributes (Order by priority)
        </KText>
        {onEditAttributes && (
          <Tooltip label="Edit attributes" hasArrow placement="top">
            <Center>
              <Icon
                as={Edit05}
                w="18px"
                h="18px"
                cursor="pointer"
                onClick={onEditAttributes}
              />
            </Center>
          </Tooltip>
        )}
      </Flex>

      <Box px="24px">
        <Divider color="secondary-100" />
      </Box>

      <Box>
        {/* Attributes list (read-only — edit via the attributes editor) */}
        <Flex flexDir="column" gap="20px" p="16px 24px 24px">
          {sortedAttributes.map(
            (attribute) =>
              attribute?.description && (
                <Flex key={attribute.id} gap="12px" alignItems="center">
                  <Stars03
                    size={20}
                    gradient={{
                      direction: 'vertical',
                      colors: [
                        { offset: '0%', color: '#797DFF' },
                        { offset: '100%', color: '#680AF5' },
                      ],
                    }}
                  />
                  <KText fontSize="14px">{attribute.description}</KText>
                </Flex>
              )
          )}
        </Flex>

        {/* Auto-reject section */}
        <Box mx="12px" mb="12px">
          <AutoRejectSection
            enabled={autoReject}
            onToggle={onAutoRejectChange}
            threshold={autoRejectThreshold}
            onThresholdChange={onThresholdChange}
          />
          {autoRejectEmailSlot}
        </Box>
      </Box>
    </Flex>
  );
};
